import {
  Constraint,
  Identifiable,
  OptimizedGantt,
  ProjectElementType,
  WorkLoadsCalendar,
} from "@planzone/dto";
import React from "react";

function isBlocked(
  element: OptimizedGantt[number],
  constraints: (Identifiable<Constraint> & {
    targetStartDate: string;
    targetEndDate: string;
    targetIsDone: boolean;
    originStartDate: string;
    originEndDate: string;
    originIsDone: boolean;
  })[],
) {
  return !!constraints.find(
    ({ targetEntityUuid, originDateType, targetDateType, originIsDone }) =>
      targetEntityUuid === element.uuid &&
      originDateType === "END" &&
      targetDateType === "START" &&
      !originIsDone,
  );
}

function renderWithTreeCtx(
  gantt: OptimizedGantt,
  isOpen: (key: string) => boolean,
  workLoads: WorkLoadsCalendar[number],
  render: (
    projectElement: OptimizedGantt[number],
    ctx: { minDate: string; maxDate: string },
    index: number,
    fullCtx: {
      [key: number]: { minDate: string; maxDate: string };
    },
    breadcrumbs: OptimizedGantt,
  ) => React.JSX.Element,
): React.ReactNode[] {
  return gantt.reduce<{
    ui: React.JSX.Element[];
    filteredCount: number;
    ctx: {
      [key: number]: {
        minDate: string;
        maxDate: string;
        filtered: boolean;
      };
    };
    breadcrumbs: OptimizedGantt;
  }>(
    ({ ui, ctx, filteredCount, breadcrumbs }, projectElement, index) => {
      const isChangingContext =
        projectElement.type === ProjectElementType.PROJECT;
      const isChangingDepth =
        projectElement.type === ProjectElementType.LIST &&
        gantt[index + 1]?.depth !== projectElement.depth;
      const filtered = ctx[projectElement.depth - 1]?.filtered;

      return {
        filteredCount: (
          isChangingContext
            ? !!projectElement.startDate && !!projectElement.endDate
            : !filtered
        )
          ? filteredCount
          : filteredCount + 1,
        ui: (
          isChangingContext
            ? !!projectElement.startDate && !!projectElement.endDate
            : !filtered
        )
          ? [
              ...ui,
              render(
                projectElement,
                ctx[
                  projectElement.depth - 1 > 0 ? projectElement.depth - 1 : 0
                ],
                index - filteredCount,
                ctx,
                breadcrumbs,
              ),
            ]
          : ui,
        ctx: isChangingContext
          ? {
              0: {
                minDate: projectElement.startDate,
                maxDate: projectElement.endDate,
                filtered:
                  !projectElement.startDate ||
                  !projectElement.endDate ||
                  !isOpen(`${workLoads.uuid}:${projectElement.uuid}`),
              },
            }
          : isChangingDepth
            ? {
                ...ctx,
                [projectElement.depth]: {
                  filtered:
                    filtered ||
                    !isOpen(`${workLoads.uuid}:${projectElement.uuid}`),
                  minDate:
                    projectElement.startDate ||
                    ctx[projectElement.depth]?.minDate ||
                    ctx[projectElement.depth - 1]?.minDate ||
                    "",
                  maxDate:
                    projectElement.endDate ||
                    ctx[projectElement.depth]?.maxDate ||
                    ctx[projectElement.depth - 1]?.maxDate ||
                    "",
                },
              }
            : ctx,
        breadcrumbs: isChangingContext
          ? [projectElement]
          : !!gantt[index + 1]
            ? gantt[index + 1].depth < projectElement.depth
              ? [
                  ...breadcrumbs.slice(
                    0,
                    breadcrumbs.length -
                      (projectElement.depth - gantt[index + 1].depth),
                  ),
                  ...(projectElement.type === ProjectElementType.LIST
                    ? [projectElement]
                    : []),
                ]
              : gantt[index + 1].depth > projectElement.depth &&
                  projectElement.type === ProjectElementType.LIST
                ? [...breadcrumbs, projectElement]
                : breadcrumbs
            : breadcrumbs,
      };
    },
    {
      filteredCount: 0,
      ui: [],
      ctx: {
        0: {
          minDate: gantt[0]?.startDate,
          maxDate: gantt[0]?.endDate,
          filtered: !gantt[0]?.startDate || !gantt[0]?.endDate,
        },
      },
      breadcrumbs: [],
    },
  ).ui;
}

export { isBlocked, renderWithTreeCtx };
